import {
  Directive,
  ElementRef,
  inject,
  input,
  output,
  OnDestroy,
  OnInit,
  Injector,
  effect,
  runInInjectionContext,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AnnotationStore } from '../../../claim-annotator/domain/annotation-store';
import { ClaimSetState } from '../../../claim-annotator/domain/claim-set-state';
import {
  OverlayAnnotationSource,
  OverlayClaimSource,
} from '../../../claim-annotator/domain/overlay-source';
import { DrawController } from '../../../claim-annotator/headless/draw-controller';
import { PageGeometry, PdfRect, ScreenRect } from '../../../claim-annotator/domain/types';
import {
  screenRectToPdfRect,
  pdfRectToScreenRect,
  clampPdfRectToPage,
} from '../../../claim-annotator/domain/coordinates';

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface OverlayDrawCompleted {
  rect: PdfRect;
  clientX: number;
  clientY: number;
  pageNumber: number;
}

export interface OverlayTextPointPicked {
  pdfX: number;
  pdfY: number;
  clientX: number;
  clientY: number;
  pageNumber: number;
}

@Directive({
  selector: '[appClaimAnnotatorOverlay]',
  standalone: true,
})
export class AnnotationOverlayDirective implements OnInit, OnDestroy {
  readonly overlayPageNumber = input.required<number>();
  readonly overlayDocumentId = input.required<string>();
  readonly overlayGeometry = input.required<PageGeometry>();
  readonly overlayDrawMode = input<'box' | 'text' | null>(null);
  /**
   * The PDF page DOM element to mount the SVG overlay into. When provided, the
   * SVG is appended to this element so it scrolls and zooms with the page and
   * the host viewer's wheel/scroll events bubble through unaffected. When omitted
   * the directive falls back to its own host element (used by unit tests).
   */
  readonly overlayPageElement = input<HTMLElement | null>(null);
  /**
   * Optional read-only annotation source. When provided it takes precedence over the
   * injected AnnotationStore for rendering. Used by read-only (modal) hosts that do not
   * provide AnnotationStore in their injector (in which case the injected store is null).
   */
  readonly overlayStore = input<OverlayAnnotationSource | null>(null);
  /** Optional read-only claim source. Takes precedence over the injected ClaimSetState for rendering. */
  readonly overlayClaimSet = input<OverlayClaimSource | null>(null);
  /**
   * When true the overlay renders boxes only: no note/delete affordances, no draw-mode
   * pointer capture, and the keydown/click editing handlers no-op. A read-only layer never
   * captures pointer events, so multiple read-only overlays coexist cleanly.
   */
  readonly overlayReadOnly = input(false);
  /** When set, only annotations whose claim_id equals this value are rendered. */
  readonly overlayClaimFilter = input<string | null>(null);
  /**
   * Box rendering style. 'hatched' draws a dashed stroke and a diagonal-line pattern fill so a
   * second layer's boxes stay visible even when they exactly overlap another layer's boxes.
   */
  readonly overlayBoxStyle = input<'solid' | 'hatched'>('solid');
  /**
   * When set, the box with this annotation id renders emphasized with a short pulse. The seq
   * nonce gives repeat jumps to the same box a fresh object identity, re-running the render
   * effect; the recreated rect restarts the CSS animation.
   */
  readonly overlayHighlight = input<{ id: string; seq: number } | null>(null);
  readonly overlayDrawCompleted = output<OverlayDrawCompleted>();
  readonly overlayTextPointPicked = output<OverlayTextPointPicked>();
  readonly overlayEditNoteRequested = output<{ id: string; clientX: number; clientY: number }>();
  readonly overlayDeleteRequested = output<string>();
  readonly overlayEditOcrRequested = output<{ id: string; clientX: number; clientY: number }>();

  private host = inject(ElementRef<HTMLElement>);
  // AnnotationStore and ClaimSetState are @Injectable() WITHOUT providedIn:'root', so in a
  // host that does not provide them (the read-only modal) these resolve to null. The overlay*
  // inputs supply read sources in that case; editing paths stay gated on the injected store.
  private injectedStore = inject(AnnotationStore, { optional: true });
  private injectedClaimSet = inject(ClaimSetState, { optional: true });
  private injector = inject(Injector);
  private draw = new DrawController();

  private svg!: SVGSVGElement;
  private draftRect: SVGRectElement | null = null;
  private sub = new Subscription();
  private cleanupKeydown: (() => void) | null = null;

  private static patternSeq = 0;
  private readonly hatchPatternIds = new Map<string, string>();
  private defs: SVGDefsElement | null = null;

  ngOnInit(): void {
    this.svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
    // pointer-events:none lets wheel/click pass through to the PDF viewer underneath
    // when the user isn't drawing. The mode-driven effect below flips it to 'auto'
    // while drawing, and individual annotation rects opt back in with pointer-events:all.
    this.svg.setAttribute(
      'style',
      'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;touch-action:none;',
    );
    this.ensureMounted();

    this.draw.attach(this.svg as unknown as HTMLElement);

    this.sub.add(
      this.draw.drawCompleted$.subscribe((ev) => {
        const pdf = clampPdfRectToPage(
          screenRectToPdfRect(ev.rect, this.overlayGeometry()),
          this.overlayGeometry(),
        );
        this.overlayDrawCompleted.emit({
          rect: pdf,
          clientX: ev.clientX,
          clientY: ev.clientY,
          pageNumber: this.overlayPageNumber(),
        });
      }),
    );

    this.sub.add(
      this.draw.textPointPicked$.subscribe((ev) => {
        const pdf = this.overlayGeometry().toPdfPoint({ x: ev.x, y: ev.y });
        this.overlayTextPointPicked.emit({
          pdfX: pdf.x,
          pdfY: pdf.y,
          clientX: ev.clientX,
          clientY: ev.clientY,
          pageNumber: this.overlayPageNumber(),
        });
      }),
    );

    runInInjectionContext(this.injector, () => {
      effect(() => {
        // A read-only overlay never enters draw mode and never captures pointer events, so
        // multiple read-only layers can coexist over the same page without stealing input.
        const readOnly = this.overlayReadOnly();
        const mode = readOnly ? null : this.overlayDrawMode();
        // ngx-extended-pdf-viewer recreates/resets page DOM as the user scrolls, which can
        // detach this overlay's SVG. Re-mount on any page-element/geometry change AND
        // whenever draw mode is toggled (the user's "Box/Text ON" gesture), so the visible
        // page always has a live draw surface; otherwise a draw-mode pointerdown falls
        // through to the viewer and scrolls the PDF instead of drawing.
        this.overlayPageElement();
        this.overlayGeometry();
        this.ensureMounted();
        this.draw.setMode(mode);
        // Capture pointer events only while actively drawing; otherwise let wheel/scroll
        // pass through so the PDF viewer can scroll/zoom normally.
        this.svg.style.pointerEvents = mode === null ? 'none' : 'auto';
      });
      effect(() => this.renderDraft());
      effect(() => this.renderAnnotations());
    });

    const onKeyDown = (e: KeyboardEvent): void => {
      // Editing-only path: no-op for read-only overlays and when no real injected store exists
      // (e.g. the modal host). Without this guard a Delete keypress would NPE on a null store.
      if (!this.injectedStore || this.overlayReadOnly()) return;
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      // Ignore when the user is typing in a form field.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const selectedId = this.injectedStore.selectedAnnotationId();
      if (!selectedId) return;
      const ann = this.injectedStore.annotations().find((a) => a.id === selectedId);
      if (!ann) return;
      // Only the overlay whose page+document the selected annotation belongs to should fire.
      if (ann.page !== this.overlayPageNumber() || ann.document_id !== this.overlayDocumentId())
        return;
      e.preventDefault();
      this.overlayDeleteRequested.emit(selectedId);
    };
    document.addEventListener('keydown', onKeyDown);
    this.cleanupKeydown = () => document.removeEventListener('keydown', onKeyDown);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.draw.detach();
    this.cleanupKeydown?.();
    if (this.svg?.parentElement) this.svg.parentElement.removeChild(this.svg);
  }

  /**
   * Ensures the SVG overlay is a child of the current page element. Mounting is
   * idempotent: a no-op when already correctly parented, and it moves (or
   * re-adds) the SVG when the page element changed or the viewer wiped it.
   */
  private ensureMounted(): void {
    const el = this.overlayPageElement() ?? this.host.nativeElement;
    if (this.svg.parentElement === el) return;
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    el.appendChild(this.svg);
  }

  private renderDraft(): void {
    const r = this.draw.currentDrag();
    if (this.draftRect && this.draftRect.parentNode === this.svg) {
      this.svg.removeChild(this.draftRect);
    }
    this.draftRect = null;
    if (!r) return;
    this.draftRect = this.makeDraftRect(r);
    this.svg.appendChild(this.draftRect);
  }

  private renderAnnotations(): void {
    Array.from(
      this.svg.querySelectorAll(
        '[data-annotation-id], [data-annotation-note], [data-annotation-delete], [data-annotation-ocr]',
      ),
    ).forEach((c) => c.remove());
    // Prefer the read-only overlay inputs; fall back to the injected stores for the live
    // annotator. When neither is available (modal without inputs) there is nothing to draw.
    const store = this.overlayStore() ?? this.injectedStore;
    const claimSet = this.overlayClaimSet() ?? this.injectedClaimSet;
    if (!store || !claimSet) return;
    const readOnly = this.overlayReadOnly();
    const filter = this.overlayClaimFilter();
    const boxStyle = this.overlayBoxStyle();
    const highlight = this.overlayHighlight();
    // selectAnnotation editing is only safe with a real injected store and when not read-only.
    const canSelect = !readOnly && !!this.injectedStore;
    const page = this.overlayPageNumber();
    const docId = this.overlayDocumentId();
    const geom = this.overlayGeometry();
    const selectedId = store.selectedAnnotationId();
    const validClaimIds = new Set(claimSet.getForDocument(docId).map((c) => c.id));

    for (const a of store.annotations()) {
      if (a.page !== page || a.document_id !== docId) continue;
      if (!validClaimIds.has(a.claim_id)) continue;
      if (filter && a.claim_id !== filter) continue;
      const screen = pdfRectToScreenRect(
        { x: a.x, y: a.y, width: a.width, height: a.height },
        geom,
      );
      const rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('data-annotation-id', a.id);
      rect.setAttribute('x', String(screen.x));
      rect.setAttribute('y', String(screen.y));
      rect.setAttribute('width', String(screen.width));
      rect.setAttribute('height', String(screen.height));
      if (boxStyle === 'hatched' && a.type !== 'text') {
        rect.setAttribute('fill', `url(#${this.hatchPatternId(a.color)})`);
        rect.setAttribute('fill-opacity', '1');
        rect.setAttribute('stroke-dasharray', '6 4');
      } else {
        rect.setAttribute('fill', a.color);
        rect.setAttribute('fill-opacity', '0.2');
      }
      rect.setAttribute('stroke', a.color);
      const highlighted = highlight?.id === a.id;
      rect.setAttribute('stroke-width', highlighted ? '4' : selectedId === a.id ? '3' : '2');
      if (highlighted) {
        // Keyframes live in styles.scss (this SVG is built imperatively, outside Angular's
        // view encapsulation, so component styles would not reach it).
        rect.classList.add('cad-annotation-pulse');
      }
      // Selecting a box is an editing affordance: only attach the click + opt back into
      // pointer events when a real injected store exists and the overlay is not read-only.
      // A read-only rect stays inert so the layer captures no pointer events.
      if (canSelect) {
        rect.setAttribute('cursor', 'pointer');
        // Annotation rects opt back into pointer events even when the parent SVG is
        // pointer-events:none, so selecting a box works without entering draw mode.
        rect.style.pointerEvents = 'all';
        rect.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.injectedStore!.selectAnnotation(a.id);
        });
      }
      this.svg.appendChild(rect);

      if (a.type === 'text' && a.text) {
        const t = document.createElementNS(SVG_NS, 'text');
        if (highlighted) {
          // A text-point annotation's rect is near-invisible; the glyph carries the pulse.
          t.classList.add('cad-annotation-pulse');
        }
        t.setAttribute('data-annotation-id', a.id);
        t.setAttribute('x', String(screen.x));
        t.setAttribute('y', String(screen.y + screen.height - 2));
        t.setAttribute('fill', a.color);
        t.setAttribute('font-size', String(Math.max(10, screen.height * 0.9)));
        t.setAttribute('font-family', 'sans-serif');
        t.textContent = a.text;
        this.svg.appendChild(t);
      }

      if (!readOnly && selectedId === a.id) {
        this.svg.appendChild(this.buildNoteButton(a.id, a.color, screen));
        this.svg.appendChild(this.buildDeleteButton(a.id, a.color, screen));
      }
    }
  }

  /**
   * Returns (creating on first use) the id of a diagonal-hatch pattern in the given color.
   * Patterns live in a <defs> child of this directive's SVG; renderAnnotations only clears
   * [data-annotation-*] nodes, so they survive re-renders. Ids are document-unique via a
   * module-level counter (one pattern per color per directive instance).
   */
  private hatchPatternId(color: string): string {
    const existing = this.hatchPatternIds.get(color);
    if (existing) return existing;
    const id = `cad-hatch-${++AnnotationOverlayDirective.patternSeq}`;
    this.hatchPatternIds.set(color, id);
    if (!this.defs) {
      this.defs = document.createElementNS(SVG_NS, 'defs') as SVGDefsElement;
      this.svg.appendChild(this.defs);
    }
    const pattern = document.createElementNS(SVG_NS, 'pattern');
    pattern.setAttribute('id', id);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', '8');
    pattern.setAttribute('height', '8');
    pattern.setAttribute('patternTransform', 'rotate(45)');
    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('x1', '0');
    line.setAttribute('y1', '0');
    line.setAttribute('x2', '0');
    line.setAttribute('y2', '8');
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', '3');
    line.setAttribute('stroke-opacity', '0.45');
    pattern.appendChild(line);
    this.defs.appendChild(pattern);
    return id;
  }

  private buildNoteButton(id: string, color: string, screen: ScreenRect): SVGGElement {
    // Positioned 22px LEFT of the corner; delete button takes the corner.
    const cx = screen.x + screen.width - 22;
    const cy = screen.y;
    const g = document.createElementNS(SVG_NS, 'g') as SVGGElement;
    g.setAttribute('data-annotation-note', id);
    g.setAttribute('cursor', 'pointer');
    g.style.pointerEvents = 'all';
    g.addEventListener('pointerdown', (ev) => ev.stopPropagation());
    g.addEventListener('click', (ev) => {
      ev.stopPropagation();
      // Emit the click position so the editor opens next to the note button (and thus the box).
      // The button swallows pointerdown to avoid starting a draw, so the host cannot otherwise
      // learn where the note was opened.
      this.overlayEditNoteRequested.emit({ id, clientX: ev.clientX, clientY: ev.clientY });
    });
    const bg = document.createElementNS(SVG_NS, 'circle');
    bg.setAttribute('cx', String(cx));
    bg.setAttribute('cy', String(cy));
    bg.setAttribute('r', '9');
    bg.setAttribute('fill', '#0f172a');
    bg.setAttribute('stroke', color);
    bg.setAttribute('stroke-width', '2');
    g.appendChild(bg);
    const t = document.createElementNS(SVG_NS, 'text');
    t.setAttribute('x', String(cx));
    t.setAttribute('y', String(cy + 3));
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('fill', '#f1f5f9');
    t.setAttribute('font-size', '10');
    t.textContent = '✎';
    g.appendChild(t);
    return g;
  }

  private buildDeleteButton(id: string, color: string, screen: ScreenRect): SVGGElement {
    const cx = screen.x + screen.width;
    const cy = screen.y;
    const g = document.createElementNS(SVG_NS, 'g') as SVGGElement;
    g.setAttribute('data-annotation-delete', id);
    g.setAttribute('cursor', 'pointer');
    g.style.pointerEvents = 'all';
    g.addEventListener('pointerdown', (ev) => ev.stopPropagation());
    g.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.overlayDeleteRequested.emit(id);
    });
    const bg = document.createElementNS(SVG_NS, 'circle');
    bg.setAttribute('cx', String(cx));
    bg.setAttribute('cy', String(cy));
    bg.setAttribute('r', '9');
    bg.setAttribute('fill', '#0f172a');
    bg.setAttribute('stroke', color);
    bg.setAttribute('stroke-width', '2');
    g.appendChild(bg);
    const r = 4;
    const x = document.createElementNS(SVG_NS, 'path');
    x.setAttribute(
      'd',
      `M${cx - r},${cy - r} L${cx + r},${cy + r} M${cx + r},${cy - r} L${cx - r},${cy + r}`,
    );
    x.setAttribute('stroke', '#f1f5f9');
    x.setAttribute('stroke-width', '2');
    x.setAttribute('stroke-linecap', 'round');
    x.setAttribute('fill', 'none');
    g.appendChild(x);
    return g;
  }

  private makeDraftRect(r: ScreenRect): SVGRectElement {
    const rect = document.createElementNS(SVG_NS, 'rect') as SVGRectElement;
    rect.setAttribute('x', String(r.x));
    rect.setAttribute('y', String(r.y));
    rect.setAttribute('width', String(r.width));
    rect.setAttribute('height', String(r.height));
    rect.setAttribute('fill', '#a78bfa');
    rect.setAttribute('fill-opacity', '0.15');
    rect.setAttribute('stroke', '#a78bfa');
    rect.setAttribute('stroke-dasharray', '4 4');
    rect.setAttribute('stroke-width', '2');
    return rect;
  }
}
